import { useState } from 'react';
import { useQueryClient } from 'react-query';

import type { VerificationChannel } from '@identity/domain/types';
import { accountStatusKey } from '@identity/presentation/constants';
import { useIdentityUserId } from '@identity/presentation/hooks';

import { PhoneAwaitingView } from './PhoneAwaitingView';
import { PhoneCaptureView } from './PhoneCaptureView';

/** What the step stores by itself: whether a link was sent **on this screen**, and to which channel. */
type SentLink = {
  channel: VerificationChannel;
  phone: string;
};

export type PhoneStepProps = {
  /** Forwarded to the capture view. The waiting view shows none: by then nothing is being chosen. */
  stepLabel?: string;
};

/**
 * The phone step: one component with two views.
 *
 * Waiting is not a separate step. The number is not confirmed until the link is opened, so the
 * backend still reports the requirement as pending and the derived step does not move. The only
 * thing this component stores is whether a link was sent on this screen, and to which channel.
 *
 * That has an intended effect: reloading returns to the capture view with an empty input, which is
 * the way to fix a wrong digit.
 *
 * @param {PhoneStepProps} props - Component properties
 */
export const PhoneStep = ({ stepLabel }: PhoneStepProps) => {
  const queryClient = useQueryClient();
  const userId = useIdentityUserId();
  const [sentTo, setSentTo] = useState<SentLink | null>(null);

  // The polling never writes to the cache: this refetch is what moves the wizard on.
  const onVerified = () => {
    queryClient.invalidateQueries(accountStatusKey(userId));
  };

  if (!sentTo) return <PhoneCaptureView onSent={setSentTo} stepLabel={stepLabel} />;

  return (
    <PhoneAwaitingView
      channel={sentTo.channel}
      onResent={({ channel }) => setSentTo({ ...sentTo, channel })}
      onUseAnotherNumber={() => setSentTo(null)}
      onVerified={onVerified}
      phone={sentTo.phone}
    />
  );
};
